export const GDN_COLOR_VARIANTS = ["black", "white", "accent"] as const;
export const GDN_COLOR_KEYS = ["button", "tag-0", "tag-1", "tag-2", "tag-3"] as const;

export type GdnColorVariant = (typeof GDN_COLOR_VARIANTS)[number];
export type GdnColorKey = "button" | `tag-${number}`;
export type GdnColorVariants = Partial<Record<GdnColorKey, GdnColorVariant>>;

const COLOR_VARIANT_SET = new Set<string>(GDN_COLOR_VARIANTS);

function assertGdnColorVariant(value: unknown): asserts value is GdnColorVariant {
  if (typeof value !== "string" || !COLOR_VARIANT_SET.has(value)) {
    throw new Error(`Invalid GDN color variant: ${String(value)}`);
  }
}

function assertGdnColorKey(value: unknown): asserts value is GdnColorKey {
  if (
    typeof value !== "string" ||
    (value !== "button" && !/^tag-(0|[1-9]\d*)$/.test(value))
  ) {
    throw new Error(`Invalid GDN color key: ${String(value)}`);
  }
}

export function getGdnColorStateKey(controlKey: unknown): GdnColorKey {
  assertGdnColorKey(controlKey);
  return controlKey;
}

export function nextGdnColorVariant(current: unknown): GdnColorVariant {
  if (current === undefined) return GDN_COLOR_VARIANTS[0];
  assertGdnColorVariant(current);

  const index = GDN_COLOR_VARIANTS.indexOf(current);
  return GDN_COLOR_VARIANTS[(index + 1) % GDN_COLOR_VARIANTS.length];
}

export function normalizeGdnColorVariants(value: unknown): GdnColorVariants {
  if (value === undefined) return {};
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    throw new Error("Invalid GDN color variants: expected an object");
  }

  const normalized: GdnColorVariants = {};
  for (const [stateKey, variant] of Object.entries(value)) {
    // Early saved scenes used "template/control" keys. Collapse them into the
    // shared control key so one tag color applies to every GDN format.
    const separatorIndex = stateKey.lastIndexOf("/");
    const controlKey = separatorIndex >= 0 ? stateKey.slice(separatorIndex + 1) : stateKey;
    if (separatorIndex >= 0) {
      const templateId = stateKey.slice(0, separatorIndex);
      if (!/^[A-Za-z][A-Za-z0-9_-]*$/.test(templateId)) {
        throw new Error(`Invalid legacy GDN color key: ${stateKey}`);
      }
    }
    const normalizedKey = getGdnColorStateKey(controlKey);
    assertGdnColorVariant(variant);
    normalized[normalizedKey] = variant;
  }
  return normalized;
}

export function normalizeGdnAccentColor(value: unknown): string | null {
  if (value === undefined || value === null) return null;
  if (typeof value !== "string" || !/^#[0-9a-f]{6}$/i.test(value)) {
    throw new Error(`Invalid GDN accent color: ${String(value)}`);
  }
  return value.toUpperCase();
}

function parseHexColor(hex: string): {r: number; g: number; b: number} {
  const match = /^#([0-9a-f]{6})$/i.exec(hex);
  if (!match) throw new Error(`Invalid GDN accent color: ${hex}`);

  return {
    r: Number.parseInt(match[1].slice(0, 2), 16),
    g: Number.parseInt(match[1].slice(2, 4), 16),
    b: Number.parseInt(match[1].slice(4, 6), 16),
  };
}

function getAccentTextColor(accentColor: string): "#000000" | "#FFFFFF" {
  const {r, g, b} = parseHexColor(accentColor);
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  return brightness >= 150 ? "#000000" : "#FFFFFF";
}

export function getGdnControlColors(
  variant: GdnColorVariant,
  accentColor: string
): {backgroundColor: string; color: string} {
  assertGdnColorVariant(variant);

  if (variant === "black") {
    return {backgroundColor: "#000000", color: "#FFFFFF"};
  }
  if (variant === "white") {
    return {backgroundColor: "#FFFFFF", color: "#000000"};
  }
  return {backgroundColor: accentColor, color: getAccentTextColor(accentColor)};
}
